import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useId, useCallback, useEffect, useLayoutEffect, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { useServerEnv } from '@hh.ru/magritte-common-is-server-env';
import { keyboardMatch, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { disableScroll } from '@hh.ru/magritte-common-modal-helper';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useNoBubbling } from '@hh.ru/magritte-common-use-no-bubbling';
import { isActionBarComponent } from '@hh.ru/magritte-ui-action-bar';
import { Breakpoint, useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Layer } from '@hh.ru/magritte-ui-layer';
import { s as styles, M as ModalContentWithHeader } from './ModalContentWithHeader-B8aTBQ1D.js';
import { useModalOrder } from './useModalOrder.js';
import { isValidTreeSelectorWrapper } from '@hh.ru/magritte-ui-tree-selector';
import '@hh.ru/magritte-common-func-utils';
import '@hh.ru/magritte-ui-divider';
import './ModalHeader.js';
import '@hh.ru/magritte-ui-title';
import '@hh.ru/magritte-common-dom-storage';

const noop = () => undefined;
const toNumber = (value) => {
    const result = parseInt(value, 10);
    return Number.isInteger(result) ? result : 0;
};
const INVISIBLE_FOR_BREAKPOINTS = [Breakpoint.XS, Breakpoint.S];
const Modal = forwardRef(({ visible, size = 'medium', title, titleAlignment = 'left', titleSize = 'large', titleMaxLines, titleElement = 'h2', titleDescription, titleDescriptionMaxLines, titleStyle, titleDescriptionStyle, children, onClose = noop, headerImage, headerHeight, host, options, actions, actionLink, footer, disableVerticalPaddings = false, disableHorizontalPaddings = false, closeByClickOutside = true, onAppear, onBeforeExit, onAfterExit, ...rest }, ref) => {
    const isServerEnv = useServerEnv();
    const enableScrollRef = useRef();
    const onAfterExitRef = useRef(onAfterExit);
    const onBeforeExitRef = useRef(onBeforeExit);
    onAfterExitRef.current = onAfterExit;
    onBeforeExitRef.current = onBeforeExit;
    const [animationTimeout, setAnimationTimeout] = useState({
        enter: 0,
        exit: 0,
    });
    const labelId = useId();
    const additionalProps = {};
    if (title) {
        additionalProps['aria-labelledby'] = labelId;
    }
    const modalRef = useRef(null);
    const overlayRef = useRef(null);
    const modalRefCallback = useMultipleRefs(ref, modalRef);
    const isPaddingsDisableAllowed = !title && !headerImage && !titleDescription && !footer && !options && !actionLink && !actions;
    const isVerticalPaddingsDisabled = disableVerticalPaddings && isPaddingsDisableAllowed;
    const isHorizontalPaddingsDisabled = disableHorizontalPaddings && isPaddingsDisableAllowed;
    const onEntering = useCallback(() => {
        if (!enableScrollRef.current) {
            enableScrollRef.current = disableScroll();
        }
        if (modalRef.current && !modalRef.current.contains(document.activeElement)) {
            modalRef.current.focus();
        }
    }, []);
    const onEntered = useCallback(() => {
        onAppear?.();
    }, [onAppear]);
    const onExit = useCallback(() => {
        onBeforeExitRef.current?.();
    }, []);
    const onExited = useCallback(() => {
        enableScrollRef.current?.();
        enableScrollRef.current = undefined;
        onAfterExitRef.current?.();
    }, []);
    const [position, count] = useModalOrder(!!visible && !isServerEnv, modalRef);
    const isOverlayHidden = (position === 0 && count > 0) || (count > 1 && position > 1);
    const { breakpoint } = useBreakpoint();
    useEffect(() => {
        if (!visible) {
            return;
        }
        const currentBreakpointVisible = !INVISIBLE_FOR_BREAKPOINTS.includes(breakpoint);
        if (!currentBreakpointVisible) {
            onExit();
            onExited();
            return;
        }
        onEntering();
        // visible не должно быть в зависимостях, т.к. этот эффект обрабатывает только изменение брейкпоинта
        // visible обрабатывается CSSTransition
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [breakpoint, onEntering, onExited, onExit]);
    useEffect(() => () => {
        enableScrollRef.current?.();
        enableScrollRef.current = undefined;
    }, [visible]);
    useLayoutEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles.animationTimeout);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);
        setAnimationTimeout({
            enter: toNumber(style.getPropertyValue('--enter-animation-duration')),
            exit: toNumber(style.getPropertyValue('--exit-animation-duration')),
        });
        document.body.removeChild(animationTimeoutElement);
    }, [setAnimationTimeout]);
    const overlayEventHandlers = useNoBubbling({
        onKeyDown: (event) => {
            if (keyboardMatch(event.nativeEvent, keyboardKeys.Escape)) {
                onClose(event);
            }
        },
        onMouseDown: (event) => {
            if (!closeByClickOutside || modalRef.current?.contains(event.target)) {
                return;
            }
            onClose(event);
        },
    });
    if (INVISIBLE_FOR_BREAKPOINTS.includes(breakpoint) || isServerEnv) {
        return null;
    }
    const modalHeaderProps = {
        title,
        titleElement,
        titleAlignment,
        titleSize,
        titleMaxLines,
        titleDescription,
        titleDescriptionMaxLines,
        titleStyle,
        titleDescriptionStyle,
        headerImage,
        headerHeight,
        options,
        actions,
        actionLink,
    };
    const isTreeSelectorChild = isValidTreeSelectorWrapper(children);
    return createPortal(jsx(CSSTransition, { appear: true, in: visible && !isServerEnv, nodeRef: overlayRef, onEntering: onEntering, onEntered: onEntered, onExit: onExit, onExited: onExited, classNames: {
            appear: styles.modalAnimationEnter,
            appearActive: styles.modalAnimationEnterActive,
            enter: styles.modalAnimationEnter,
            enterActive: styles.modalAnimationEnterActive,
            exit: styles.modalAnimationExit,
            exitActive: styles.modalAnimationExitActive,
        }, timeout: animationTimeout, unmountOnExit: true, children: jsx(Layer, { layer: "modal", children: jsx("div", { ...overlayEventHandlers, ref: overlayRef, className: styles.modalOverlay, style: isOverlayHidden ? { backgroundColor: 'transparent' } : {}, "data-qa": "modal-overlay", children: jsxs("div", { ...rest, ...additionalProps, role: "dialog", className: classnames(styles.modal, {
                        [styles.sizeSmall]: size === 'small',
                        [styles.sizeMedium]: size === 'medium',
                        [styles.modalFullHeight]: isTreeSelectorChild,
                        [styles.noVerticalPaddings]: isVerticalPaddingsDisabled,
                        [styles.noHorizontalPaddings]: isHorizontalPaddingsDisabled,
                    }), ref: modalRefCallback, tabIndex: 0, children: [jsx(ModalContentWithHeader, { ...modalHeaderProps, labelId: labelId, isTreeSelectorChild: isTreeSelectorChild, children: children }), !!footer && (jsx("div", { className: styles.modalFooter, "data-qa": "modal-footer", children: isActionBarComponent(footer)
                                ? cloneElement(footer, { type: footer.props.type || 'modal' })
                                : footer }))] }) }) }) }), host || document.body);
});
Modal.displayName = 'Modal';

export { Modal };
//# sourceMappingURL=Modal.js.map
